import React, { useState, useEffect } from "react";
import "./Sign-In.scss";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { ToastContainer, Zoom } from "react-toastify";
import AuthLayout from "./Auth-Layout";
import ContactModal from "../../LandingPage/src/components/ContactModal/ContactModal";
import { loginUser } from "../../redux/slices/sliceActions";
import { setRememberMe } from "../../redux/slices/userSlice";
import customToasts from "../assets/reusableComponents/myToasts";

const SignIn = ({ showContact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isContactOpen, setIsContactOpen] = useState(showContact);

  useEffect(() => {
    if (showContact) setIsContactOpen(true);
  }, [showContact]);

  const [logins, setLogins] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [passError, setPassError] = useState("");

  const {
    user,
    loading,
    rememberMe,
  } = useSelector((state) => state.landlordslice);

  const from = location.state?.from?.pathName || "/auth/landlord";
  const [check, setCheck] = useState(rememberMe || false);

  // Check if rememberMe is true and redirect if user data exists
  useEffect(() => {
    if (rememberMe && user?.data?.userType) {
      if (user.data.userType === "tenant") {
        navigate("/auth/tenant");
      } else if (user.data.userType === "caretaker") {
        navigate("/auth/caretaker");
      } else {
        navigate("/auth/landlord");
      }
    }
  }, [rememberMe, user, navigate]);

  // Sync local checkbox with global rememberMe state
  useEffect(() => {
    setCheck(rememberMe);
  }, [rememberMe]);

  // Kenyan phone number validation
  const validateKenyanPhone = (phone) => {
    const kenyanPhoneRegex = /^(07|01)\d{8}$/;
    return kenyanPhoneRegex.test(phone);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLogins((prev) => ({ ...prev, [name]: value }));

    if (name === "username") {
      if (value && !validateKenyanPhone(value)) {
        setPhoneError("Phone number must be in format 07XXXXXXXX or 01XXXXXXXX");
      } else {
        setPhoneError("");
      }
    }

    if (name === "password") {
      setPassError("");
    }
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setCheck(isChecked);
    dispatch(setRememberMe(isChecked));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedPhone = logins.username.trim();
    if (!validateKenyanPhone(trimmedPhone)) {
      setPhoneError("Phone number must be in format 07XXXXXXXX or 01XXXXXXXX");
      return;
    }

    if (!logins.password.trim()) {
      setPassError("Please enter your password");
      return;
    }

    customToasts
      .promise(
        dispatch(
          loginUser({
            phone: `+254${trimmedPhone.substring(1)}`,
            password: logins.password.trim(),
            rememberMe: check,
          })
        ).unwrap(),
        {
          pending: "Signing in...",
          success: "Login successful!",
          error: "Login failed",
        }
      )
      .then((result) => {
        const userType = result?.data?.user?.user_type;
        if (userType === "tenant") {
          navigate("/auth/tenant");
        } else if (userType === "caretaker") {
          navigate("/auth/caretaker");
        } else {
          navigate(from);
        }
      })
      .catch((error) => {
        console.error("Login error:", error);
      });
  };

  return (
    <>
      <AuthLayout
        title="Welcome back"
        subtitle="Don't have an account?"
        subtitleLinkText="Sign up"
        onSubtitleAction={() => setIsContactOpen(true)}
      >
        <form className="sign-in-form" onSubmit={handleSubmit} noValidate>
          {/* Phone Number Field */}
          <div className="form-group">
            <label className="input-label" htmlFor="signin-phone">
              Phone Number
            </label>
            <div className="input-wrapper">
              <input
                id="signin-phone"
                name="username"
                type="tel"
                className={`auth-input ${phoneError ? "has-error" : ""}`}
                placeholder="07XXXXXXXX or 01XXXXXXXX"
                value={logins.username}
                onChange={handleInputChange}
                autoComplete="username tel"
                required
              />
            </div>
            {phoneError && <span className="input-error-text">{phoneError}</span>}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label className="input-label" htmlFor="signin-password">
              Password
            </label>
            <div className="input-wrapper">
              <input
                id="signin-password"
                name="password"
                type={showPassword ? "text" : "password"}
                className={`auth-input has-toggle ${passError ? "has-error" : ""}`}
                placeholder="Enter your password"
                value={logins.password}
                onChange={handleInputChange}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {passError && <span className="input-error-text">{passError}</span>}
          </div>

          {/* Options Row: Remember Me & Forgot Password */}
          <div className="form-options-row">
            <label className="remember-me-container">
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={check}
                onChange={handleCheckboxChange}
              />
              <span className="checkbox-text">Remember me</span>
            </label>

            <Link to="/forgot-password" className="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
          >
            {loading ? <span className="btn-spinner" /> : "Sign in"}
          </button>
        </form>

        <ToastContainer
          position="bottom-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          transition={Zoom}
        />
      </AuthLayout>
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default SignIn;
